<template>
  <CardWithTitle
    :item-id="itemId"
    :items="items"
    :map-keys="mapKeys"
    :request-status="requestStatus"
    :options-data="cardOptions"
    :has-data-to-export="hasDataToExport"
    class="make-a-copy"
    @call-option-menu="callOptionMenu"
    @reload="getData"
  >
    <v-data-table
      v-if="hasItems"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.bot`]="{ item }">
        <span>{{ item.bot }}</span>
      </template>
      <template v-slot:[`item.agent`]="{ item }">
        <span>{{ item.agent }}</span>
      </template>
      <template
        v-slot:[`item.status`]="{ item }"
      >
        <div class="status">
          <span :style="{ color: item.statusColor, fontSize: '2rem'}">&#8226;</span>
          <span> {{ item.status }}</span>
        </div>
      </template>
      <template v-slot:[`item.ticketsCount`]="{ item }">
        <span>{{ item.ticketsCount }}</span>
      </template>
      <template v-slot:[`item.averageResponseTime`]="{ item }">
        <span>{{ item.averageResponseTime }}</span>
      </template>
      <template v-slot:[`item.averageAttendanceTime`]="{ item }">
        <span>{{ item.averageAttendanceTime }}</span>
      </template>
    </v-data-table>

    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import blipModel from '@/mixins/blip-model';
import { mapActions } from 'vuex';
import { timeToSeconds, secondsToTime } from '@/utils/formatter';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import Badge from '@/components/Badge';

export default {
  name: 'BlipMonitoringAttendantsTable',
  components: {
    CardWithTitle,
    Badge,
  },
  mixins: [
    exportModel,
    cardsModel,
    blipModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    hasDataToExport: {
      type: Boolean,
      default: true,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      rawSeries: [],
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
      cardFilters: {},
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('components.BlipMonitoringAttendantsTable.bot'), value: 'bot' },
        { text: this.$t('components.BlipMonitoringAttendantsTable.agent'), value: 'agent' },
        { text: this.$t('components.BlipMonitoringAttendantsTable.status'), value: 'status' },
        { text: this.$t('components.BlipMonitoringAttendantsTable.ticketsCount'), value: 'ticketsCount' },
        { text: this.$t('components.BlipMonitoringAttendantsTable.averageResponseTime'), value: 'averageResponseTime' },
        { text: this.$t('components.BlipMonitoringAttendantsTable.averageAttendanceTime'), value: 'averageAttendanceTime' },
      ];
    },
    mapKeys() {
      return {
        bot: this.$t('components.BlipMonitoringAttendantsTable.bot'),
        agent: this.$t('components.BlipMonitoringAttendantsTable.agent'),
        status: this.$t('components.BlipMonitoringAttendantsTable.status'),
        ticketsCount: this.$t('components.BlipMonitoringAttendantsTable.ticketsCount'),
        averageResponseTime: this.$t('components.BlipMonitoringAttendantsTable.averageResponseTime'),
        averageAttendanceTime: this.$t('components.BlipMonitoringAttendantsTable.averageAttendanceTime'),
      };
    },
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    hasItems() {
      return Boolean(this.items.length);
    },
    cardOptions() {
      if (this.hasDataToExport) {
        return this.baseCardOptions.concat(this.exportWithXLSXOptionsData(this.items)).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
      }
      return this.baseCardOptions;
    },
    items() {
      return this.rawSeries.flatMap((obj) => {
        const bot = this.getBotName(obj.to, this.cardFilters);
        const resourceItems = obj.resource.items.map((item) => {
          const statusColor = this.getColorCode(item.status);
          return {
            bot,
            agent: item.agentName,
            status: item.status === 'Invisible' ? 'Em pausa' : item.status,
            ticketsCount: item.ticketsCount,
            averageResponseTime: secondsToTime(timeToSeconds(item.averageResponseTime)),
            averageAttendanceTime: secondsToTime(timeToSeconds(item.averageAttendanceTime)),
            statusColor,
          };
        });
        return resourceItems;
      });
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'getAppliedCardFilter',
      'hasNoBlipBotAndContractFiltered',
    ]),
    getColorCode(status) {
      switch (status) {
      case 'Offline':
        return 'red';
      case 'Invisible':
        return '#FDA94C';
      case 'Online':
        return '#1EA842';
      default:
        return 'black';
      }
    },
    exportWithXLSXOptionsData(items) {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));

      exportExtensionOptions.push({
        order: exportExtensionOptions.length + 1,
        name: 'XLSX',
        function: () => (this.exportComponentXLSX(items)),
      });

      return [
        {
          order: 5,
          name: this.$t('mixins.exportModel.label.exportLabel'),
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        this.items = [];
        return;
      }

      this.cardFilters = await this.getAppliedCardFilter(this.itemId);

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then((responses) => {
          if (!options?.silentRefresh) {
            this.requestStatus = 'loading';
          }

          if (this.totalResponseItems(responses)) {
            this.requestStatus = 'emptyData';
            return;
          }

          this.rawSeries = responses;

          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.items = [];
          this.requestStatus = error.message;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.status {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: flex-start;
}

::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
